import React, { useState, useContext, useEffect } from "react";
import { Check, Underline, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SessionContext } from "../context/sessionContext";
import { useUser } from "../context/UserContext";
import { plans, oneTimeStudy, studentPlan } from "../assets/data/pricingData";
import { Helmet } from "react-helmet";
import { pricing } from "../assets/data/metaData";
import Navbar from "../components/mainNavBar";
import HomeFooter from "../components/homefooter";
import Cookies from "js-cookie";

const footnotes = {
  1: "Project Allowance resets each billing cycle (monthly or quarterly, depending on plan); there is no rollover for unused research projects",
  2: "Research Units (RUs) are calculated as the number of survey responses generated in a project. This may be estimated as the product of sample size (n) and survey length (# of distinct questions to be answered)",
  3: "Unused Avatar Access questions roll over from period to period (requires active subscription)",
  4: "Initiation of new projects requires the Researcher role; Collaborators may view and interact with project deliverables, but may not initiate new projects",
  5: "Cancellations will be processed at the first billing period following the notification period (after the completion of the plan's term commitment)",
  6: "Billing Cycle: Starter bills quarterly (1 project/quarter) with no minimum term - cancel anytime. Growth and Scale bill monthly with 6- and 12-month minimum terms, respectively.",
};

const FootnoteRef = ({ number }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    const footnoteElement = document.getElementById(`footnote-${number}`);
    if (footnoteElement) {
      footnoteElement.scrollIntoView({ behavior: "smooth", block: "center" });
      footnoteElement.style.backgroundColor = "rgba(37, 99, 235, 0.2)";
      setTimeout(() => {
        footnoteElement.style.backgroundColor = "transparent";
      }, 2000);
    }
  };

  return (
    <span
      className="footnote-ref"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <sup onClick={handleClick}>{number}</sup>
      {showTooltip && (
        <div className="footnote-tooltip">
          <div className="footnote-tooltip-arrow" />
          {footnotes[number]}
        </div>
      )}
    </span>
  );
};

const parseTextWithFootnotes = (text) => {
  const parts = text.split(/(¹|²|³|⁴|⁵|⁶|\d+(?=\s*$))/);

  const superscriptMap = {
    "¹": 1,
    "²": 2,
    "³": 3,
    "⁴": 4,
    "⁵": 5,
    "⁶": 6,
  };

  return parts.map((part, index) => {
    if (superscriptMap[part]) {
      return <FootnoteRef key={index} number={superscriptMap[part]} />;
    } else if (/^\d+$/.test(part.trim())) {
      return <FootnoteRef key={index} number={parseInt(part)} />;
    }
    return part;
  });
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, session } = useContext(SessionContext);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin, roleName, isLoading: roleLoading } = useUser();
  const [showOneTimePopup, setShowOneTimePopup] = useState(false);
  const [showStudentPopup, setShowStudentPopup] = useState(false);
  // const [llmMode, setLlmMode] = useState("openai");

  // const getDisplayPrice = (plan) => {
  //   if (llmMode === "local") {
  //     const bumped = plan.price + 200;
  //     return `$${bumped.toLocaleString()}`;
  //   }
  //   return plan.priceDisplay;
  // };
  const getDisplayPrice = (plan) => plan.priceDisplay;

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      const token = Cookies.get("token");
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/project/getuserPlanAndProject`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", token: token },
            credentials: "include",
          },
        );

        const data = await response.json();
        if (data.status === "success" && data.planDetails?.plan_name) {
          setCurrentPlan(data.planDetails.plan_name);
        }
      } catch (err) {
        console.error("Error fetching current plan:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentPlan();
  }, [isAuthenticated]);

  const redirectToStripe = async (plan) => {
    try {
      const token = Cookies.get("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          body: JSON.stringify({
            planName: plan.name,
            amount: plan.price,
            userId: session?.user?.user_id || session?.user?.id,
          }),
        },
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error("Stripe URL missing in response:", data);
        alert("Failed to create payment session. Please try again.");
      }
    } catch (err) {
      console.error("Stripe Error:", err);
      alert("An error occurred while processing payment. Please try again.");
    }
  };

  const handlePlanSelect = (plan) => {
    localStorage.setItem(
      "selectedPlan",
      JSON.stringify({
        planName: plan.name,
        amount: plan.price,
      }),
    );

    if (isAuthenticated) {
      redirectToStripe(plan);
    } else {
      navigate("/sign-up");
    }
  };

  const isPlanSelected = (planName) => {
    return currentPlan === planName;
  };

  const PlanPopup = ({ plan, onClose }) => (
    <div className="plan-popup-overlay" onClick={onClose}>
      <div className="plan-popup-card" onClick={(e) => e.stopPropagation()}>
        <button className="plan-popup-close" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="plan-popup-header">
          <h3 className="plan-popup-name">{plan.name}</h3>
          <p className="plan-popup-subtitle">{plan.subtitle}</p>
          <div className="plan-popup-price-row">
            <span className="plan-popup-price">{plan.priceDisplay}</span>
          </div>
        </div>

        <button
          className="plan-popup-choose-btn"
          onClick={() => {
            handlePlanSelect(plan);
            onClose();
          }}
        >
          Choose Plan
        </button>

        <div>
          <p className="plan-popup-features-label">What's Included</p>
          <ul className="plan-popup-feature-list">
            {plan.features.map((feature, index) => (
              <li key={index} className="plan-popup-feature-item">
                <span className="plan-popup-feature-dot"></span>
                <span className="plan-popup-feature-text">
                  {parseTextWithFootnotes(feature.text)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pricing-page">
      <Helmet>
        <title>{pricing.title} </title>
        <meta name="description" content={pricing.description} data-rh="true" />
        <meta
          name="og:description"
          content={pricing.description}
          data-rh="true"
        />
        <meta name="keywords" content={pricing.keywords} data-rh="true" />
        <meta property="og:title" content={pricing.title} data-rh="true" />
        <meta property="og:image" content={pricing.image} data-rh="true" />
        <meta property="og:url" content={pricing.url} data-rh="true" />
        <meta
          property="og:publisher"
          content={pricing.publisher}
          data-rh="true"
        />
        <meta property="og:author " content={pricing.author} data-rh="true" />
        <meta
          property="og:site_name"
          content={pricing.site_name}
          data-rh="true"
        />
        <meta property="og:locale" content={pricing.locale} data-rh="true" />
        <meta property="og:type" content={pricing.type} data-rh="true" />
        <link rel="canonical" href={pricing.canonical} data-rh="true" />
      </Helmet>
      <Navbar />

      <div className="pricing-container">
        <div className="pricing-header">
          <h1 className="pricing-title">Choose Your Plan</h1>
          <br />
          <p className="pricing-subtitle">
            {isAuthenticated && !isAdmin && !roleLoading && (
              <div className="current-plan-notice">
                You are logged in as <strong>{roleName}</strong>. Only
                organization admins can purchase or change plans. Please contact
                your admin for billing updates.
              </div>
            )}
          </p>
        </div>

        {/*
        <div className="llm-toggle-wrapper">
          <div className="llm-toggle-track">
            <button
              className={`llm-toggle-btn ${llmMode === "openai" ? "llm-toggle-btn--active" : ""}`}
              onClick={() => setLlmMode("openai")}
            >
              OpenAI
            </button>
            <button
              className={`llm-toggle-btn ${llmMode === "local" ? "llm-toggle-btn--active" : ""}`}
              onClick={() => setLlmMode("local")}
            >
              Local LLM
            </button>
          </div>
        </div>
        */}

        <div className="pricing-cards">
          {plans.map((plan, index) => {
            const isSelected = isPlanSelected(plan.name);
            const showSelected = isSelected;
            const showRecommended = plan.highlighted && !isSelected;

            return (
              <div
                key={index}
                className={`pricing-card ${
                  plan.highlighted ? "pricing-card--highlighted" : ""
                } ${isSelected ? "pricing-card--selected" : ""}`}
              >
                {showSelected && (
                  <div className="pricing-badge pricing-badge--selected">
                    CURRENT PLAN
                  </div>
                )}
                {showRecommended && (
                  <div className="pricing-badge">{plan.badge}</div>
                )}

                <div className="pricing-card-header">
                  <h3 className="pricing-card-title">{plan.name}</h3>
                  <p className="pricing-card-subtitle">{plan.subtitle}</p>
                  <div className="pricing-card-price">
                    <span className="price-amount">
                      {getDisplayPrice(plan)}
                    </span>
                    <span className="price-period">{plan.period}</span>
                  </div>
                  <p className="pricing-card-billed">{plan.billedText}</p>
                </div>

                <button
                  className={`pricing-card-button ${
                    plan.highlighted ? "pricing-card-button--highlighted" : ""
                  } ${isSelected ? "pricing-card-button--selected" : ""}`}
                  disabled={roleLoading}
                  onClick={() => {
                    if (!isAuthenticated) {
                      handlePlanSelect(plan);
                      return;
                    }

                    if (!isAdmin) {
                      alert("Only organization admins can manage billing.");
                      return;
                    }

                    handlePlanSelect(plan);
                  }}
                  title={
                    isAuthenticated && !isAdmin
                      ? "Only organization admins can manage billing"
                      : ""
                  }
                >
                  {roleLoading
                    ? "Checking access..."
                    : !isAuthenticated
                      ? "Choose Plan"
                      : !isAdmin
                        ? "Admin Required"
                        : plan.buttonText}
                </button>

                <div className="pricing-card-features">
                  <p className="pricing-label">Features</p>
                  <ul className="pricing-list">
                    {plan.features.map((feature, idx) => {
                      let iconSrc = "/json-media/icons/singlegreen.svg";
                      let iconSize = "20px";

                      if (feature.tier === "yellow") {
                        iconSrc = "/json-media/icons/yellowtick.svg";
                      } else if (feature.tier === "double") {
                        iconSrc = "/json-media/icons/doublegreen.svg";
                        iconSize = "30px";
                      }
                      let iconMarginRight = "0px";

                      if (
                        feature.tier === "yellow" ||
                        feature.tier === "single"
                      ) {
                        iconMarginRight = "10px";
                      }
                      return (
                        <li key={idx} className="pricing-item">
                          <img
                            src={iconSrc}
                            alt="feature icon"
                            style={{
                              flexShrink: 0,
                              width: iconSize,
                              height: iconSize,
                              marginTop: "2px",
                              marginRight: iconMarginRight,
                            }}
                          />
                          <span className="pricing-text">
                            {parseTextWithFootnotes(feature.text)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        <div className="pricing-alternatives">
          <h3 className="alternatives-title">Looking for other options?</h3>

          <div className="alternatives-cards-stacked">
            <div className="alternative-plans-stack">
              <button
                className="btn-one-time"
                onClick={() => setShowOneTimePopup(true)}
              >
                Pay as You Go
              </button>

              <button
                className="btn-student"
                onClick={() => setShowStudentPopup(true)}
              >
                Student Plan
              </button>
            </div>
          </div>
        </div>

        <section className="pricing-features">
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <div className="placeholder icon-placeholder">
                  <img
                    className="img-responsive"
                    alt="Research Acceleration"
                    src="/json-media/img/homepage/icon-1.svg"
                  />
                </div>
              </div>
              <h4>Research Acceleration</h4>
              <p>Get comprehensive insights in hours instead of weeks.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <div className="placeholder icon-placeholder">
                  <img
                    className="img-responsive"
                    alt="Unlimited Capabilities"
                    src="/json-media/img/homepage/icon-2.svg"
                  />
                </div>
              </div>
              <h4>Unlimited Capabilities</h4>
              <p>
                No constraints on question counts, audience targeting, or
                conversation topics.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <div className="placeholder icon-placeholder">
                  <img
                    className="img-responsive"
                    alt="Living Intelligence"
                    src="/json-media/img/homepage/icon-3.svg"
                  />
                </div>
              </div>
              <h4>Living Intelligence</h4>
              <p>
                Move beyond static datasets to ongoing, customized analysis.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <div className="placeholder icon-placeholder">
                  <img
                    className="img-responsive"
                    alt="Statistical Rigor"
                    src="/json-media/img/homepage/icon-4.svg"
                  />
                </div>
              </div>
              <h4>Statistical Rigor</h4>
              <p>
                Full rigor of research with factor analysis, clustering and
                segmentation.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <div className="placeholder icon-placeholder">
                  <img
                    className="img-responsive"
                    alt="Cost Optimization"
                    src="/json-media/img/homepage/icon-5.svg"
                  />
                </div>
              </div>
              <h4>Cost Optimization</h4>
              <p>
                Focus on your primary / sweet spot on validated opportunities.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <div className="placeholder icon-placeholder">
                  <img
                    className="img-responsive"
                    alt="Complementary Toolkit"
                    src="/json-media/img/homepage/icon-6.svg"
                  />
                </div>
              </div>
              <h4>Complementary Toolkit</h4>
              <p>Designed to work alongside your existing research methods.</p>
            </div>
          </div>
        </section>

        <div className="pricing-footnotes">
          <ol className="footnotes-list">
            {Object.entries(footnotes).map(([number, text]) => (
              <li key={number} id={`footnote-${number}`}>
                {text}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <HomeFooter />

      {showOneTimePopup && (
        <PlanPopup
          plan={oneTimeStudy}
          onClose={() => setShowOneTimePopup(false)}
        />
      )}

      {showStudentPopup && (
        <PlanPopup
          plan={studentPlan}
          onClose={() => setShowStudentPopup(false)}
        />
      )}
    </div>
  );
};
export default PricingPage;
